import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  InputGroup,
  FormControl,
  Spinner,
  Badge,
  Modal,
  Pagination,
  Form,
} from "react-bootstrap";
import { Avatar } from "@mui/joy";
import { get_all_bids, cancel_bid_action } from "../reduxData/user/userAction";
import debounce from "lodash.debounce";

const BidManagement = () => {
  const [bids, setBids] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchBids = async (currentPage, search) => {
    setIsLoading(true);
    const data = await get_all_bids(currentPage, 20, search);
    if (data) {
      setBids(data.data || []);
      setTotalPages(data.pagination.pages);
      setPage(data.pagination.page);
    }
    setIsLoading(false);
  };

  const debouncedFetch = useCallback(
    debounce((nextValue) => fetchBids(1, nextValue), 500),
    [],
  );

  useEffect(() => {
    fetchBids(page, searchTerm);
  }, [page]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    debouncedFetch(val);
  };

  const handleOpenCancelModal = (bid) => {
    setSelectedBid(bid);
    setShowCancelModal(true);
  };

  const confirmCancelBid = async () => {
    if (!selectedBid) return;
    setIsProcessing(true);

    const success = await cancel_bid_action(selectedBid._id);

    if (success) {
      setShowCancelModal(false);
      fetchBids(page, searchTerm);
    }
    setIsProcessing(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge bg="primary">Active</Badge>;
      case "won":
        return <Badge bg="success">Won</Badge>;
      case "loss":
        return <Badge bg="secondary">Loss</Badge>;
      case "cancelled":
        return <Badge bg="danger">Cancelled</Badge>;
      default:
        return (
          <Badge bg="light" text="dark">
            {status}
          </Badge>
        );
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-0">Bid Management</h3>
          <p className="text-muted mb-0">
            View all bids and manage cancellations.
          </p>
        </div>
        <Button
          variant="outline-primary"
          onClick={() => fetchBids(page, searchTerm)}
        >
          <i className="fas fa-sync me-2"></i>Refresh
        </Button>
      </div>

      {/* --- Search --- */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <i className="fas fa-search text-muted"></i>
            </InputGroup.Text>
            <FormControl
              placeholder="Search by Item Name, Username, Email, or Amount..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </InputGroup>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <Table striped hover responsive className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Item</th>
                    <th>User</th>
                    <th>Amount</th>
                    <th>Date Placed</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bids.length > 0 ? (
                    bids.map((bid) => (
                      <tr
                        key={bid._id}
                        className={
                          bid.bid_status === "cancelled" ? "opacity-50" : ""
                        }
                      >
                        <td>
                          <div className="d-flex align-items-center">
                            {bid.product?.photos?.[0] && (
                              <img
                                src={bid.product.photos[0]}
                                alt=""
                                className="rounded me-2"
                                style={{
                                  width: 40,
                                  height: 40,
                                  objectFit: "cover",
                                }}
                              />
                            )}
                            <div>
                              <div
                                className="fw-bold text-truncate"
                                style={{ maxWidth: "200px" }}
                              >
                                {bid.product?.item_name || "Unknown Item"}
                              </div>
                              <div className="small text-muted">
                                ID: {bid.product?._id.slice(-6)}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="d-flex align-items-center">
                            <Avatar
                              src={bid.userId?.profile_pic}
                              size="sm"
                              className="me-2"
                            />
                            <div>
                              <div>{bid.userId?.user_name || "Unknown"}</div>
                              <div
                                className="small text-muted"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {bid.userId?.email}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="fw-bold font-monospace">
                          ${bid.amount?.toLocaleString()}
                        </td>

                        <td>
                          {new Date(bid.createdAt).toLocaleDateString()}
                          <br />
                          <span className="small text-muted">
                            {new Date(bid.createdAt).toLocaleTimeString()}
                          </span>
                        </td>

                        <td>
                          {bid.auto_bid ? (
                            <Badge bg="info" className="text-dark">
                              Auto
                            </Badge>
                          ) : (
                            <Badge bg="secondary">Manual</Badge>
                          )}
                        </td>

                        <td>{getStatusBadge(bid.bid_status)}</td>

                        <td className="text-end">
                          {bid.bid_status === "active" ||
                          bid.bid_status === "won" ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleOpenCancelModal(bid)}
                              title="Cancel this bid"
                            >
                              <i className="fas fa-ban"></i>
                            </Button>
                          ) : (
                            <span className="text-muted small">--</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center p-5 text-muted">
                        No bids found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </>
          )}
        </div>

        {totalPages > 1 && (
          <div className="card-footer bg-white d-flex justify-content-center pt-3">
            <Pagination>
              <Pagination.First
                onClick={() => setPage(1)}
                disabled={page === 1}
              />
              <Pagination.Prev
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              />

              {[...Array(totalPages)].map((_, idx) => {
                const pNum = idx + 1;
                if (
                  pNum === 1 ||
                  pNum === totalPages ||
                  (pNum >= page - 2 && pNum <= page + 2)
                ) {
                  return (
                    <Pagination.Item
                      key={pNum}
                      active={pNum === page}
                      onClick={() => setPage(pNum)}
                    >
                      {pNum}
                    </Pagination.Item>
                  );
                }
                if (pNum === page - 3 || pNum === page + 3)
                  return <Pagination.Ellipsis key={pNum} />;
                return null;
              })}

              <Pagination.Next
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              />
              <Pagination.Last
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
              />
            </Pagination>
          </div>
        )}
      </div>

      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <i className="fas fa-exclamation-triangle me-2"></i>
            Cancel Bid
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBid && (
            <div>
              <p className="fw-bold text-danger">
                Warning: This action cannot be undone.
              </p>
              <p>
                Are you sure you want to cancel the bid of{" "}
                <strong>${selectedBid.amount}</strong> placed by{" "}
                <strong>{selectedBid.userId?.user_name}</strong> on item{" "}
                <strong>{selectedBid.product?.item_name}</strong>?
              </p>
              <div className="alert alert-warning small">
                <strong>What happens next:</strong>
                <ul className="mb-0 ps-3 mt-1">
                  <li>The bid status will be set to 'cancelled'.</li>
                  <li>
                    The Auction price will revert to the previous highest bid.
                  </li>
                  <li>
                    The User's Auto-Bid settings for this item (if any) will be
                    removed.
                  </li>
                  <li>
                    All users viewing the auction will see the price update
                    immediately.
                  </li>
                </ul>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelModal(false)}
            disabled={isProcessing}
          >
            Abort
          </Button>
          <Button
            variant="danger"
            onClick={confirmCancelBid}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Spinner as="span" size="sm" animation="border" />
            ) : (
              "Confirm Cancel"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BidManagement;
